/**
 * Worker pool for the SDF closest-point distance pass.
 *
 * The per-voxel `closestPointToPoint` queries dominate SDF generation (~20 s, embarrassingly
 * parallel). This pool fans the masked voxel set out across N module workers: each worker
 * deserializes a copy of the BVH and computes distances for a contiguous slice, then transfers
 * the results back. The app is not cross-origin isolated, so geometry/BVH are structure-cloned
 * to workers (no SharedArrayBuffer) — a few MB × N, negligible against the query time saved.
 */

import { MeshBVH } from 'three-mesh-bvh';

export type SerializedBvh = ReturnType<typeof MeshBVH.serialize>;

/** Lattice descriptor a worker needs to decode a linear voxel index into a world point. */
export type SdfWorkerGrid = {
  ox: number;
  oy: number;
  oz: number;
  cellSize: number;
  cx: number;
  cy: number;
  cz: number;
};

export type SdfWorkerGeometry = {
  position: Float32Array;
  bvh: SerializedBvh;
  grid: SdfWorkerGrid;
};

/** One closed part for the union-signed-distance mode (flat, clonable). */
export type SerializedSignedPart = {
  position: Float32Array;
  bvh: SerializedBvh;
  pn: {
    weldOf: Int32Array;
    weldedCount: number;
    faceNormals: Float32Array;
    vertexNormals: Float32Array;
    edgeKeys: Float64Array;
    edgeNormals: Float32Array;
    borderEdge: Uint8Array;
    borderVertex: Uint8Array;
  };
  boxMin: { x: number; y: number; z: number };
  boxMax: { x: number; y: number; z: number };
};

type WorkerRequest =
  | (SdfWorkerGeometry & { id: number; indices: Int32Array })
  | {
      id: number;
      union: true;
      parts: SerializedSignedPart[];
      grid: SdfWorkerGrid;
      indices: Int32Array;
      slabMm: number;
    };
type WorkerResponse =
  | { id: number; distances: Float32Array }
  | { id: number; values: Float32Array; flags: Uint8Array };

export class SdfWorkerPool {
  private workers: Worker[] = [];
  private nextId = 1;

  constructor(private readonly size: number) {}

  get workerCount(): number {
    return this.size;
  }

  private ensureWorkers(): void {
    if (this.workers.length > 0) return;
    for (let i = 0; i < this.size; i++) {
      this.workers.push(
        new Worker(new URL('./sdfDistanceWorker.ts', import.meta.url), { type: 'module' }),
      );
    }
  }

  /**
   * Compute the closest-surface distance for each voxel in `maskedIndices` (grid-linear order).
   * Returns a Float32Array aligned to `maskedIndices`. Rejects if any worker errors so the
   * caller can fall back to the synchronous path.
   */
  async computeDistances(geo: SdfWorkerGeometry, maskedIndices: Int32Array): Promise<Float32Array> {
    const n = maskedIndices.length;
    const out = new Float32Array(n);
    if (n === 0) return out;

    this.ensureWorkers();
    const workerCount = Math.min(this.workers.length, n);
    const chunk = Math.ceil(n / workerCount);

    const jobs: Promise<void>[] = [];
    for (let w = 0; w < workerCount; w++) {
      const start = w * chunk;
      const end = Math.min(n, start + chunk);
      if (start >= end) break;
      jobs.push(this.runChunk(this.workers[w], geo, maskedIndices.slice(start, end), out, start));
    }
    await Promise.all(jobs);
    return out;
  }

  /**
   * Union-signed-distance mode: each masked voxel gets min-over-parts SIGNED
   * distance (`values`, aligned to `maskedIndices`) plus an ambiguity flag
   * (near-tangential closest feature — sign untrustworthy). Rejects on worker
   * error so the caller can fall back to the synchronous path.
   */
  async computeUnionSignedDistances(
    parts: SerializedSignedPart[],
    grid: SdfWorkerGrid,
    maskedIndices: Int32Array,
    slabMm = 0,
  ): Promise<{ values: Float32Array; flags: Uint8Array }> {
    const n = maskedIndices.length;
    const values = new Float32Array(n);
    const flags = new Uint8Array(n);
    if (n === 0) return { values, flags };

    this.ensureWorkers();
    // The union payload (all parts + BVHs + pseudo-normals) is structure-cloned
    // per worker — cap the fan-out so the transient copies stay bounded.
    const workerCount = Math.min(this.workers.length, n, 8);
    const chunk = Math.ceil(n / workerCount);

    const jobs: Promise<void>[] = [];
    for (let w = 0; w < workerCount; w++) {
      const start = w * chunk;
      const end = Math.min(n, start + chunk);
      if (start >= end) break;
      const indices = maskedIndices.slice(start, end);
      jobs.push(
        this.runJob(this.workers[w], { id: 0, union: true, parts, grid, indices, slabMm }, [indices.buffer], (data) => {
          if (!('values' in data)) throw new Error('sdf worker: unexpected legacy response');
          values.set(data.values, start);
          flags.set(data.flags, start);
        }),
      );
    }
    await Promise.all(jobs);
    return { values, flags };
  }

  private runChunk(
    worker: Worker,
    geo: SdfWorkerGeometry,
    indices: Int32Array,
    out: Float32Array,
    offset: number,
  ): Promise<void> {
    return this.runJob(worker, { id: 0, ...geo, indices }, [indices.buffer], (data) => {
      if (!('distances' in data)) throw new Error('sdf worker: unexpected union response');
      out.set(data.distances, offset);
    });
  }

  private runJob(
    worker: Worker,
    request: WorkerRequest,
    transfer: Transferable[],
    onResult: (data: WorkerResponse) => void,
  ): Promise<void> {
    const id = this.nextId++;
    return new Promise<void>((resolve, reject) => {
      const cleanup = () => {
        worker.removeEventListener('message', onMessage);
        worker.removeEventListener('error', onError);
      };
      const onMessage = (ev: MessageEvent<WorkerResponse>) => {
        if (ev.data.id !== id) return; // not our job (worker is shared across generations)
        cleanup();
        try {
          onResult(ev.data);
          resolve();
        } catch (error) {
          reject(error instanceof Error ? error : new Error(String(error)));
        }
      };
      const onError = (err: ErrorEvent) => {
        cleanup();
        reject(err.error ?? new Error(err.message));
      };
      worker.addEventListener('message', onMessage);
      worker.addEventListener('error', onError);

      // Payloads are structure-cloned (main keeps them); the index slice is transferred.
      worker.postMessage({ ...request, id }, transfer);
    });
  }

  terminate(): void {
    this.workers.forEach((w) => w.terminate());
    this.workers = [];
  }
}

let cachedPool: SdfWorkerPool | null | undefined;

/** Lazy singleton. Returns null when workers are unavailable (tests / SSR / creation failure). */
export function getSdfWorkerPool(): SdfWorkerPool | null {
  if (cachedPool !== undefined) return cachedPool;
  try {
    if (typeof Worker === 'undefined') {
      cachedPool = null;
      return cachedPool;
    }
    // closestPointToPoint is memory-bandwidth bound, so throughput plateaus a few cores in —
    // extra workers just contend for RAM. Leave one core for the main thread and cap at 8
    // (beyond that we measured no gain on this workload).
    const cores = (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 4;
    cachedPool = new SdfWorkerPool(Math.max(1, Math.min(12, cores)));
  } catch {
    cachedPool = null;
  }
  return cachedPool;
}
